import { getSharedLang, type Lang } from './base-i18n';

/**
 * screen-lock i18n (2026-08-19) — 잠금 화면·설정 화면 문구.
 *
 * LoginPage / MfaVerifyPage 와 같은 지역 사전 방식이다(cycle 52 패턴). 잠금 화면은 다른 화면과
 * 문구를 나눠 쓰지 않으므로 layout/settings 사전에 섞지 않고 여기 모아 둔다.
 * 없는 키·언어는 ko 로 떨어진다(cycle 46 fallback chain).
 */

type Vars = Record<string, string | number>;

const DICT: Record<Lang, Record<string, string>> = {
  ko: {
    'a11y.dialog': '화면잠금',
    'title.locked': '화면이 잠겼습니다',
    'title.soon': '곧 자동 로그아웃됩니다',
    'sub.fallback': '비밀번호를 입력해 잠금을 해제하세요',
    'field.label': '비밀번호',
    'field.placeholder': '비밀번호 입력',
    'field.show': '비밀번호 표시',
    'field.hide': '비밀번호 숨기기',
    'btn.unlock': '잠금 해제',
    'err.invalid': '비밀번호가 올바르지 않습니다 · 남은 시도 {left}회',
    'err.network': '확인에 실패했습니다 · 잠시 후 다시 시도하세요',
    'err.mfa': '2단계 인증 계정입니다 · 로그아웃 후 다시 로그인하세요',
    'sso.sub': '회사 SSO로 로그인한 계정입니다',
    'sso.relogin': '로그아웃하고 다시 로그인',
    'foot.logout_in': '자동 로그아웃까지 ',
    'foot.no_logout': '자동 로그아웃 없음',
    'foot.logout': '로그아웃',
    'preview.tag': '미리보기 · 5초 후 닫힘',
    'preview.close': '닫기',
    'warn.title': '{min}분 후 화면이 잠깁니다',
    'warn.body': '자리를 비운 지 오래됐습니다. 계속 사용하시겠습니까?',
    'warn.lock_now': '지금 잠그기',
    'warn.keep': '계속 사용하기',
  },
  en: {
    'a11y.dialog': 'Screen lock',
    'title.locked': 'Screen locked',
    'title.soon': 'Signing out soon',
    'sub.fallback': 'Enter your password to unlock',
    'field.label': 'Password',
    'field.placeholder': 'Enter your password',
    'field.show': 'Show password',
    'field.hide': 'Hide password',
    'btn.unlock': 'Unlock',
    'err.invalid': 'Incorrect password · {left} attempts left',
    'err.network': 'Could not verify · please try again shortly',
    'err.mfa': 'This account uses two-factor sign-in · sign out and sign in again',
    'sso.sub': 'This account signs in with company SSO',
    'sso.relogin': 'Sign out and sign in again',
    'foot.logout_in': 'Signing out in ',
    'foot.no_logout': 'No automatic sign-out',
    'foot.logout': 'Sign out',
    'preview.tag': 'Preview · closes in 5s',
    'preview.close': 'Close',
    'warn.title': 'Screen locks in {min} min',
    'warn.body': "You've been away for a while. Keep working?",
    'warn.lock_now': 'Lock now',
    'warn.keep': 'Keep working',
  },
  ja: {
    'a11y.dialog': '画面ロック',
    'title.locked': '画面がロックされました',
    'title.soon': 'まもなく自動ログアウトします',
    'sub.fallback': 'パスワードを入力して解除してください',
    'field.label': 'パスワード',
    'field.placeholder': 'パスワードを入力',
    'field.show': 'パスワードを表示',
    'field.hide': 'パスワードを非表示',
    'btn.unlock': 'ロック解除',
    'err.invalid': 'パスワードが正しくありません · 残り{left}回',
    'err.network': '確認に失敗しました · しばらくしてから再試行してください',
    'err.mfa': '2段階認証のアカウントです · ログアウトして再ログインしてください',
    'sso.sub': '社内SSOでログインしたアカウントです',
    'sso.relogin': 'ログアウトして再ログイン',
    'foot.logout_in': '自動ログアウトまで ',
    'foot.no_logout': '自動ログアウトなし',
    'foot.logout': 'ログアウト',
    'preview.tag': 'プレビュー · 5秒後に閉じます',
    'preview.close': '閉じる',
    'warn.title': '{min}分後に画面がロックされます',
    'warn.body': '長時間操作がありません。続けて使用しますか？',
    'warn.lock_now': '今すぐロック',
    'warn.keep': '続けて使用',
  },
};

export function tLock(key: string, vars?: Vars): string {
  const lang = getSharedLang();
  const raw = DICT[lang]?.[key] ?? DICT.ko[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => String(vars[name] ?? m));
}
